/**
 * "Tags" tab — OPTIONAL free-form name / value rows ("add tags or leave empty").
 * The platform always adds product, environment, deployment_id and managed_by itself.
 */
import clsx from 'clsx'
import formStyles from '@/pages/NewDeploymentPage.module.css'
import { MAX_TAGS } from './appriseTags'
import styles from './AppriseCreateVm.module.css'
import type { AppriseVmFormApi } from './useAppriseVmForm'

export function AppriseTagsTab({ form }: { form: AppriseVmFormApi }) {
  const rows = form.tagRows
  const err = (k: string) => form.validation.errors[k]
  const update = (i: number, patch: Partial<{ key: string; value: string }>) =>
    form.setTagRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))
  return (
    <div className={styles.fullRow}>
      <p style={{ margin: '0 0 12px', fontSize: '0.8125rem', color: 'var(--aptean-text-secondary)' }}>
        Tags are optional — add tags to categorise resources, or leave this empty. Names cannot contain &lt; &gt; % &amp; \ ? / and cannot
        start with azure, microsoft or windows (at most {MAX_TAGS} tags). The platform also adds product, environment, deployment_id and
        managed_by tags automatically.
      </p>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className={formStyles.label}>Tags</span>
        <button type="button" className={formStyles.addTagBtn} disabled={rows.length >= MAX_TAGS}
          onClick={() => form.setTagRows([...rows, { key: '', value: '' }])}>+ Add tag</button>
      </div>
      {err('operations.tags') && <span className={formStyles.errorMsg}>{err('operations.tags')}</span>}
      {rows.length === 0 ? (
        <div className={formStyles.emptyHint}>No tags — click Add tag to label the resources, or leave this empty</div>
      ) : (
        <div className={formStyles.tagList}>
          {rows.map((r, i) => (
            <div key={i}>
              <div className={formStyles.tagRow}>
                <input className={clsx(formStyles.tagKeyInput, err(`operations.tags.${i}.key`) && formStyles.inputError)} placeholder="Name"
                  aria-label={`Tag ${i + 1} name`} value={r.key} onChange={(e) => update(i, { key: e.target.value })} />
                <span className={formStyles.tagSep}>:</span>
                <input className={clsx(formStyles.tagValInput, err(`operations.tags.${i}.value`) && formStyles.inputError)} placeholder="Value"
                  aria-label={`Tag ${i + 1} value`} value={r.value} onChange={(e) => update(i, { value: e.target.value })} />
                <button type="button" className={formStyles.removeBtn} aria-label={`Remove tag ${i + 1}`}
                  onClick={() => form.setTagRows(rows.filter((_, idx) => idx !== i))}>×</button>
              </div>
              {(err(`operations.tags.${i}.key`) || err(`operations.tags.${i}.value`)) && (
                <span className={formStyles.errorMsg}>{err(`operations.tags.${i}.key`) ?? err(`operations.tags.${i}.value`)}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
